// Performance Optimization Utilities

// Image lazy loading and optimization
export const optimizeImages = () => {
  // Add loading="lazy" to images that aren't already optimized
  const images = document.querySelectorAll('img:not([loading])');
  images.forEach(img => {
    if (img.getBoundingClientRect().top > window.innerHeight) {
      img.setAttribute('loading', 'lazy');
    }
  });
};

// Preload critical resources
export const preloadCriticalResources = () => {
  const criticalResources = [
    // Add your critical images/fonts here
    'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap'
  ];

  criticalResources.forEach(resource => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = resource;
    if (resource.includes('.woff2')) {
      link.as = 'font';
      link.type = 'font/woff2';
      link.crossOrigin = 'anonymous';
    } else if (resource.includes('css')) {
      link.as = 'style';
    }
    document.head.appendChild(link);
  });
};

// Service Worker Registration for Caching
export const registerServiceWorker = () => {
  if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js')
        .then(registration => {
          console.log('SW registered: ', registration);
        })
        .catch(registrationError => {
          console.log('SW registration failed: ', registrationError);
        });
    });
  }
};

// Optimize third-party scripts
export const loadThirdPartyScripts = () => {
  // Load non-critical scripts after page load
  window.addEventListener('load', () => {
    // Google Analytics - Replace with your measurement ID
    // loadGoogleAnalytics('G-XXXXXXXXXX');
    
    // Other third-party scripts can be loaded here
    setTimeout(() => {
      // Load any other non-critical scripts
      console.log('Third-party scripts loaded');
    }, 2000);
  });
};

// Critical CSS inlining helper
export const inlineCriticalCSS = () => {
  // This is typically done at build time, but can be enhanced here
  const criticalCSS = `
    /* Critical above-the-fold styles */
    .header { display: flex; }
    .hero { background: linear-gradient(135deg, #eff6ff 0%, #f9fafb 100%); }
  `;
  
  const style = document.createElement('style');
  style.innerHTML = criticalCSS;
  document.head.appendChild(style);
};

// Resource prefetching for next pages
export const prefetchNextPages = () => {
  const prefetchRoutes = [
    '/products',
    '/about',
    '/contact'
  ];

  // Prefetch on hover or after initial load
  prefetchRoutes.forEach(route => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = route;
    document.head.appendChild(link);
  });
};

// Bundle splitting and code optimization
export const optimizeBundleLoading = () => {
  // Dynamic imports for heavy components
  const loadHeavyComponents = async () => {
    if (window.innerWidth > 768) {
      // Load desktop-specific components
      await import('../components/ui/dialog');
      await import('../components/ui/navigation-menu');
    }
  };

  // Load heavy components after initial render
  setTimeout(loadHeavyComponents, 100);
};

// Memory leak prevention
export const preventMemoryLeaks = () => {
  // Clean up event listeners on page unload
  window.addEventListener('beforeunload', () => {
    // Remove any global event listeners
    // Clear any intervals or timeouts
    // Disconnect any observers
  });
};

// Network optimization
export const optimizeNetworkRequests = () => {
  // Implement request batching for API calls
  const pendingRequests = new Map();
  
  return (url, options) => {
    const key = `${url}-${JSON.stringify(options)}`;
    
    if (pendingRequests.has(key)) {
      return pendingRequests.get(key);
    }
    
    const promise = fetch(url, options)
      .finally(() => {
        pendingRequests.delete(key);
      });
    
    pendingRequests.set(key, promise);
    return promise;
  };
};

// Font optimization
export const optimizeFonts = () => {
  // Use font-display: swap for web fonts
  const fontLink = document.createElement('link');
  fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
  fontLink.rel = 'stylesheet';
  fontLink.media = 'print';
  fontLink.onload = function() {
    this.media = 'all';
  };
  document.head.appendChild(fontLink);
};

// Initialize all performance optimizations
export const initPerformanceOptimizations = () => {
  // Run immediately
  optimizeImages();
  preloadCriticalResources();
  optimizeFonts();
  
  // Run after DOM is ready
  document.addEventListener('DOMContentLoaded', () => {
    prefetchNextPages();
    optimizeBundleLoading();
  });
  
  // Run after page load
  window.addEventListener('load', () => {
    loadThirdPartyScripts();
    preventMemoryLeaks();
  });
  
  console.log('Performance optimizations initialized');
};

export default {
  initPerformanceOptimizations,
  optimizeImages,
  preloadCriticalResources,
  registerServiceWorker,
  optimizeNetworkRequests
};